import React from 'react';
import './LogoBrand.css';

interface LogoBrandProps {
  /** 'light' = dark text on light bg (default). 'dark' = white text on dark bg. */
  variant?: 'light' | 'dark';
  /** Icon size in px (default 28). Wordmark scales proportionally. */
  size?: number;
  /** Whether to show the wordmark alongside the mark (default true). */
  showWordmark?: boolean;
  className?: string;
}

/**
 * Portal PMO — Bridge Mark + Wordmark
 *
 * The mark is 5 vertical pillars in an arch profile representing the
 * 5-bridge governance workflow (Bridge 0 → Bridge 4). The arch above
 * the pillars represents portfolio-level oversight.
 *
 * Usage:
 *   <LogoBrand />                   — full logo, light theme
 *   <LogoBrand variant="dark" />    — full logo, dark bg
 *   <LogoBrand showWordmark={false} size={24} /> — icon only
 */
export default function LogoBrand({
  variant = 'light',
  size = 28,
  showWordmark = true,
  className = '',
}: LogoBrandProps) {
  const isDark = variant === 'dark';
  const markColor = isDark ? '#ffffff' : '#cc785c';
  const textColor = isDark ? '#faf9f5' : '#141413';
  const labelColor = isDark ? 'rgba(255,255,255,0.6)' : '#cc785c';

  // Arc: connects the tops of all 5 pillars
  // viewBox 32×32, bars at x: 0,7,14,21,28 (w=4), heights: 12,18,24,18,12
  const arcPath = 'M 2 20 Q 16 1 30 20';

  return (
    <span className={`lb-root ${isDark ? 'lb-root--dark' : ''} ${className}`} style={{ '--lb-size': `${size}px` } as React.CSSProperties}>
      {/* Bridge Mark — inline SVG so it scales with CSS var */}
      <svg
        className="lb-mark"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        {/* Arch */}
        <path d={arcPath} stroke={markColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
        {/* 5 pillars */}
        <rect x="0"  y="20" width="4" height="12" rx="1.5" fill={markColor} />
        <rect x="7"  y="14" width="4" height="18" rx="1.5" fill={markColor} />
        <rect x="14" y="8"  width="4" height="24" rx="1.5" fill={markColor} />
        <rect x="21" y="14" width="4" height="18" rx="1.5" fill={markColor} />
        <rect x="28" y="20" width="4" height="12" rx="1.5" fill={markColor} />
      </svg>

      {showWordmark && (
        <span className="lb-wordmark" aria-label="Portal PMO">
          <span className="lb-wordmark__portal" style={{ color: textColor }}>Portal</span>
          <span className="lb-wordmark__pmo"    style={{ color: labelColor }}>PMO</span>
        </span>
      )}
    </span>
  );
}
